import { useEffect, useState } from 'react';
import { getAccountScanRuns, getScanRunDetail, type ScanRun, type ScanRunDetail } from '../account';
import { RunDetailModal, engineLabel } from './ScanHistory';
import { useAccount } from '../account/context';

/**
 * Sidebar: navigation + recent scans. On xl+ a sticky right rail, inline below.
 *
 * The two halves used to look identical, so the history read as four more menu
 * items (B2). They are now separate blocks: the nav is a plain menu, each scan
 * is a card carrying its own result (readiness + how often AI named you), which
 * is what makes it worth clicking. `refreshKey` refetches.
 */
export default function RecentScans({ refreshKey = 0, embedded = false, onScan, onAbout, onFaq, onProgress }: {
  refreshKey?: number;
  /**
   * Rendered as a dashboard page rather than as the floating sidebar.
   *
   * The sidebar is `fixed` on xl so it can sit beside the scan page. Inside the
   * dashboard it is the page content, so it has to flow normally or it would
   * hover over the rail it was navigated to from.
   */
  embedded?: boolean;
  onScan?: () => void; onAbout?: () => void; onFaq?: () => void; onProgress?: () => void;
}): JSX.Element | null {
  const [rows, setRows] = useState<ScanRun[] | null>(null);
  const [detail, setDetail] = useState<ScanRunDetail | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const { account } = useAccount();
  const plan: 'free' | 'pro' = account?.subscription.tier === 'pro' ? 'pro' : 'free';

  useEffect(() => {
    getAccountScanRuns({ page: 1 }).then((d) => setRows(d.rows.slice(0, 8))).catch(() => setRows([]));
  }, [refreshKey]);
  if (!rows || rows.length === 0) return null;

  const open = (id: string): void => {
    setDetail(null);
    setLoadingId(id);
    getScanRunDetail(id).then(setDetail).catch(() => {}).finally(() => setLoadingId(null));
  };

  // A border at rest, not only on hover: these read as a list of headings until
  // you happen to move the mouse over one, and a menu that only looks like a
  // menu when touched is a menu people do not find.
  const navItem = 'flex items-center justify-between w-full text-left text-sm text-text-secondary '
    + 'border border-[#70707D] bg-warm-dark-2/60 rounded-lg px-3 py-2 '
    + 'hover:text-cyan hover:border-cyan/60 hover:bg-warm-dark-2 transition';
  const label = 'font-mono text-[10px] uppercase tracking-[0.14em] text-text-secondary/60';

  const body = (
    <>
      {/*
        The menu, only where there is no other menu.

        As the floating sidebar on the scan page this is the only navigation on
        screen. Inside the dashboard every one of these four is an item in the
        rail two hundred pixels to the left, so repeating them is a second menu
        competing with the first.
      */}
      {!embedded && (
        <>
          <div className={`${label} px-2.5 pb-1.5`}>Menu</div>
          <nav className="flex flex-col gap-1.5">
            <button onClick={onScan} className={navItem}>Scan</button>
            <button onClick={onAbout} className={navItem}>How AI citation really works</button>
            <button onClick={onFaq} className={navItem}>FAQ</button>
            <button onClick={onProgress} className={navItem}>My scans &amp; progress</button>
          </nav>
        </>
      )}

      <div className={embedded ? '' : 'mt-5 pt-4 border-t border-warm-dark-2'}>
        {/* The page head already says "Recent scans", and "all" is the rail's
            own Scans & progress item. Both only earn their place in the
            sidebar, where nothing else is labelling this list. */}
        {!embedded && (
          <div className="flex items-baseline justify-between px-2.5 pb-2">
            <span className={label}>Recent scans</span>
            <button onClick={onProgress} className="font-mono text-[10px] text-cyan/80 hover:text-cyan transition">all →</button>
          </div>
        )}

        <div className="flex flex-col gap-1.5">
          {rows.map((r) => {
            /*
             * Visibility first, footprint only if there is no visibility.
             *
             * This preferred footprint, and a run whose footprint is 0 of 0
             * (asked nothing) overwrote a visibility of 3 of 25. The sidebar
             * then said "0 named" about a scan whose own detail screen said
             * three. Visibility is the result this product is about; footprint
             * is the side question.
             */
            const named = r.visTotal
              ? { n: r.visMentioned ?? 0, of: r.visTotal }
              : r.fpTotal
                ? { n: r.fpMentioned ?? 0, of: r.fpTotal }
                : null;
            return (
              <button
                key={r.id}
                onClick={() => open(r.id)}
                className="text-left border border-[#70707D] rounded-lg px-3 py-2 bg-warm-dark-2/60 hover:border-cyan/60 hover:bg-warm-dark-2 transition group"
              >
                {/*
                  Readiness is gone from this list.
                
                  It is the same number for every run of a domain, so repeating
                  it down a sidebar says nothing and competes with the one figure
                  that does change. What belongs here is whether AI named you.
                */}
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm text-text-primary group-hover:text-cyan transition truncate">{r.domain}</span>
                  {named && (
                    <span
                      className={`font-mono text-xs shrink-0 ${named.n > 0 ? 'text-[#6BE07A]' : 'text-[#E0574A]'}`}
                      title={`named in ${named.n} of ${named.of} questions`}
                    >
                      {named.n > 0 ? `${named.n} named` : '0 named'}
                    </span>
                  )}
                </div>
                {/* Which questions this run asked. Without it two runs of the same
                    domain are indistinguishable, and they are often two different
                    measurements. */}
                {r.querySetName && (
                  <div className="font-mono text-[11px] text-text-secondary/80 truncate mt-0.5" title={r.querySetName}>
                    {r.querySetName}
                  </div>
                )}
                <div className="flex items-center gap-2 mt-1 font-mono text-[11px] text-text-secondary">
                  <span>{new Date(r.createdAt).toLocaleDateString()}</span>
                  <span className="text-text-secondary/40">·</span>
                  <span>{engineLabel(r.engine)}</span>
                  {named && <><span className="text-text-secondary/40">·</span><span>of {named.of}</span></>}
                  {loadingId === r.id && <span className="ml-auto text-cyan/70">opening…</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  // As a dashboard page it is the content, so it flows and uses the width it is
  // given. As a sidebar it floats beside the scan page.
  if (embedded) {
    return (
      <>
        <div className="max-w-xl">{body}</div>
        {detail && <RunDetailModal run={detail} onClose={() => setDetail(null)} plan={plan} />}
      </>
    );
  }

  return (
    <>
      <aside className="hidden xl:flex flex-col fixed top-28 right-6 w-72 max-h-[75vh] overflow-y-auto z-30 bg-warm-dark-2/40 border border-warm-dark-2 rounded-xl p-3 backdrop-blur-sm">
        {body}
      </aside>
      <div className="xl:hidden w-full max-w-md">{body}</div>

      {detail && <RunDetailModal run={detail} onClose={() => setDetail(null)} plan={plan} />}
    </>
  );
}
